/**
 * Group-by aggregation, generated against the cached table.
 *
 * Grouping changes the *shape* of the result: the grid no longer shows the
 * columns of the SQL the user wrote. That mismatch is confusing if unlabelled,
 * so a grouped result carries its own schema (`ViewResult.columns`) and the
 * cell shows a GROUPED banner naming the grouping.
 *
 * Composition with Filter & Sort is fixed and deliberate:
 *   filter (on source columns) -> group -> sort (on output columns)
 * which is SQL's own order and the only one that lets you sort by an aggregate.
 */

import { classifyType, isNumericClass, quoteIdent } from '@/lib/values';
import type { ColumnSchema, DuckDbType, TypeClass } from '@/types/api';
import type { SortSpec } from '@/cache/types';

export type AggregateFn = 'count' | 'countDistinct' | 'sum' | 'avg' | 'min' | 'max';

export interface GroupColumn {
  readonly id: string;
  readonly column: string;
}

export interface AggregateSpec {
  readonly id: string;
  readonly column: string;
  readonly fn: AggregateFn;
}

export interface GroupState {
  readonly enabled: boolean;
  readonly groups: readonly GroupColumn[];
  readonly aggregates: readonly AggregateSpec[];
}

export const EMPTY_GROUP: GroupState = { enabled: false, groups: [], aggregates: [] };

let sequence = 0;
export function groupId(prefix: string): string {
  sequence += 1;
  return `${prefix}${sequence}_${Math.random().toString(36).slice(2, 7)}`;
}

/* ---------------------------------------------------------- aggregates --- */

export interface AggregateMeta {
  readonly value: AggregateFn;
  readonly label: string;
  readonly classes: readonly TypeClass[];
  /** `count(*)` needs no column. */
  readonly needsColumn: boolean;
}

const ANY: readonly TypeClass[] = [
  'number',
  'bigint',
  'boolean',
  'string',
  'temporal',
  'nested',
  'binary',
];
const NUMERIC: readonly TypeClass[] = ['number', 'bigint'];
const ORDERED: readonly TypeClass[] = ['number', 'bigint', 'string', 'temporal'];

export const AGGREGATES: readonly AggregateMeta[] = [
  { value: 'count', label: 'count', classes: ANY, needsColumn: false },
  { value: 'countDistinct', label: 'count distinct', classes: ANY, needsColumn: true },
  { value: 'sum', label: 'sum', classes: NUMERIC, needsColumn: true },
  { value: 'avg', label: 'avg', classes: NUMERIC, needsColumn: true },
  { value: 'min', label: 'min', classes: ORDERED, needsColumn: true },
  { value: 'max', label: 'max', classes: ORDERED, needsColumn: true },
];

export function aggregatesFor(type: DuckDbType | undefined): readonly AggregateMeta[] {
  const cls = classifyType(type as DuckDbType);
  return AGGREGATES.filter((entry) => entry.classes.includes(cls));
}

export function aggregateAllowed(fn: AggregateFn, type: DuckDbType | undefined): boolean {
  return aggregatesFor(type).some((entry) => entry.value === fn);
}

export function coerceAggregate(fn: AggregateFn, type: DuckDbType | undefined): AggregateFn {
  if (aggregateAllowed(fn, type)) return fn;
  return aggregatesFor(type)[0]?.value ?? 'count';
}

export function defaultAggregateFor(type: DuckDbType | undefined): AggregateFn {
  return isNumericClass(classifyType(type as DuckDbType)) ? 'sum' : 'count';
}

/** Output column name for an aggregate. Stable, readable, collision-free. */
export function aggregateAlias(spec: AggregateSpec): string {
  if (spec.fn === 'count') return 'count';
  const prefix = spec.fn === 'countDistinct' ? 'count_distinct' : spec.fn;
  return `${prefix}_${spec.column}`;
}

/** Result type of an aggregate, used for alignment and cell formatting. */
export function aggregateType(spec: AggregateSpec, source: DuckDbType | undefined): DuckDbType {
  switch (spec.fn) {
    case 'count':
    case 'countDistinct':
      return 'BIGINT';
    case 'avg':
      return 'DOUBLE';
    case 'sum':
      // DuckDB widens integer sums; DOUBLE would lose precision on BIGINT.
      return classifyType(source as DuckDbType) === 'bigint' ? 'HUGEINT' : 'DOUBLE';
    default:
      return source ?? 'VARCHAR';
  }
}

/* -------------------------------------------------------------- state ---- */

export function isGroupActive(state: GroupState): boolean {
  return state.enabled && (state.groups.length > 0 || state.aggregates.length > 0);
}

export function newGroupColumn(column: string): GroupColumn {
  return { id: groupId('g'), column };
}

export function newAggregate(column: string, type: DuckDbType | undefined): AggregateSpec {
  return { id: groupId('a'), column, fn: defaultAggregateFor(type) };
}

export function moveGroupColumn(
  groups: readonly GroupColumn[],
  id: string,
  delta: -1 | 1,
): GroupColumn[] {
  const next = [...groups];
  const index = next.findIndex((entry) => entry.id === id);
  const target = index + delta;
  if (index === -1 || target < 0 || target >= next.length) return next;
  const moved = next[index];
  const displaced = next[target];
  if (!moved || !displaced) return next;
  next[index] = displaced;
  next[target] = moved;
  return next;
}

/* ---------------------------------------------------------------- SQL ---- */

/** The schema a grouped query produces. Drives the grid and cell formatting. */
export function groupedSchema(
  state: GroupState,
  types: ReadonlyMap<string, DuckDbType>,
): ColumnSchema[] {
  const columns: ColumnSchema[] = state.groups.map((entry) => ({
    name: entry.column,
    type: types.get(entry.column) ?? 'VARCHAR',
    typeText: types.get(entry.column) ?? 'VARCHAR',
    nullable: true,
  }));

  const seen = new Set(columns.map((column) => column.name));

  for (const spec of state.aggregates) {
    let alias = aggregateAlias(spec);
    // Two aggregates can collide (count over two columns); keep both usable.
    let suffix = 2;
    while (seen.has(alias)) {
      alias = `${aggregateAlias(spec)}_${suffix}`;
      suffix += 1;
    }
    seen.add(alias);

    const type = aggregateType(spec, types.get(spec.column));
    columns.push({ name: alias, type, typeText: type, nullable: spec.fn !== 'count' });
  }

  return columns;
}

function aggregateExpression(spec: AggregateSpec): string {
  const col = quoteIdent(spec.column);
  switch (spec.fn) {
    case 'count':
      return 'count(*)';
    case 'countDistinct':
      return `count(DISTINCT ${col})`;
    case 'sum':
      return `sum(${col})`;
    case 'avg':
      return `avg(CAST(${col} AS DOUBLE))`;
    case 'min':
      return `min(${col})`;
    case 'max':
      return `max(${col})`;
    default:
      return 'count(*)';
  }
}

/**
 * SELECT list and GROUP BY for a grouped query.
 *
 * Every identifier goes through `quoteIdent`, including aliases derived from
 * column names, so a column called `we"ird` cannot break the statement.
 */
export function groupSelectClause(
  state: GroupState,
  types: ReadonlyMap<string, DuckDbType>,
): { select: string; groupBy: string } {
  const schema = groupedSchema(state, types);
  const parts: string[] = [];

  state.groups.forEach((entry, index) => {
    const alias = schema[index]?.name ?? entry.column;
    parts.push(`${quoteIdent(entry.column)} AS ${quoteIdent(alias)}`);
  });

  state.aggregates.forEach((spec, index) => {
    const alias = schema[state.groups.length + index]?.name ?? aggregateAlias(spec);
    parts.push(`${aggregateExpression(spec)} AS ${quoteIdent(alias)}`);
  });

  if (parts.length === 0) parts.push('count(*) AS "count"');

  const groupBy =
    state.groups.length > 0
      ? ` GROUP BY ${state.groups.map((entry) => quoteIdent(entry.column)).join(', ')}`
      : '';

  return { select: parts.join(', '), groupBy };
}

/**
 * Sorting a grouped result can only reference its output columns; a sort left
 * over from the ungrouped view would be invalid SQL.
 */
export function sortForGroupedSchema(
  sort: readonly SortSpec[],
  schema: readonly ColumnSchema[],
): SortSpec[] {
  const names = new Set(schema.map((column) => column.name));
  return sort.filter((spec) => names.has(spec.column));
}

export function describeGroup(state: GroupState): string {
  if (!isGroupActive(state)) return '';
  const groups = state.groups.map((entry) => entry.column);
  const aggregates = state.aggregates.length;
  const by = groups.length > 0 ? `by ${groups.join(', ')}` : 'over all rows';
  return `${by} · ${aggregates || 1} aggregate${aggregates === 1 ? '' : 's'}`;
}
